import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import theme from '../adminTheme';
import AdminModal from '../components/AdminModal';
import AdminSpinner from '../components/AdminSpinner';
import { useToast } from '../context/ToastContext';
import {
  aplicarRecordsLiftingCast,
  buscarPersonasParaRecord,
  crearAtletaParaRecord,
  normalizarUrlLiftingCast,
  previsualizarRecordsLiftingCast,
} from '../../../services/liftingcastService';

const ESTADOS_APLICABLES = ['nuevo', 'supera'];

const TIPOS = { squat: 'Squat', bench_press: 'Bench Press', deadlift: 'Deadlift', total: 'Total' };

const EDADES = {
  subjunior: 'Sub-Junior',
  junior: 'Junior',
  open: 'Open',
  master_i: 'Master I',
  master_ii: 'Master II',
  master_iii: 'Master III',
  master_iv: 'Master IV',
  master_v: 'Master V',
};

const MODALIDADES = {
  raw: 'Raw',
  classic_raw: 'Classic Raw',
  squat: 'Only Squat',
  bench: 'Only Bench',
  deadlift: 'Only Deadlift',
};

const AMBITOS = { nacional: 'Nacional', mundial: 'Mundial', regional: 'Regional' };

const formatearKg = (kg) => (kg == null ? '—' : `${Number(kg)} kg`);

const busquedaInicial = (nombre) => (nombre || '').split(/\s+/).filter(Boolean).slice(0, 2).join(' ');

const FILTROS_COMPETENCIA = [
  { id: 'ambos', label: 'Ambos' },
  { id: 'tested', label: 'Solo tested' },
  { id: 'untested', label: 'Solo untested' },
];

const estilos = {
  formSync: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: 16,
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    marginBottom: 20,
  },
  ayuda: { color: '#666', fontSize: 13, margin: '4px 0 0 0', flexBasis: '100%' },
  meetCard: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    padding: 16,
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    marginBottom: 16,
  },
  meetNombre: { margin: 0, fontSize: 17, fontWeight: 700, color: '#111' },
  meetMeta: { color: '#666', fontSize: 13, marginTop: 4 },
  campoMeet: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#374151', fontWeight: 600 },
  filtroCompetencia: { display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden', alignSelf: 'flex-start' },
  opcionFiltro: {
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
    borderRight: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
  },
  opcionFiltroActiva: { background: '#1a4d8c', color: '#fff' },
  secundario: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  filaResaltada: { background: '#fffbeb', boxShadow: 'inset 3px 0 0 #f59e0b' },
  filaInactiva: { opacity: 0.55 },
  barraGuardar: {
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    padding: '14px 16px',
    marginTop: 16,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
  },
  persona: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#fff',
    cursor: 'pointer',
    textAlign: 'left',
    marginBottom: 8,
    fontSize: 14,
  },
  personaActiva: { borderColor: '#1a4d8c', background: '#eff6ff' },
  listaPersonas: { maxHeight: 320, overflowY: 'auto', marginTop: 12 },
};

const BadgeEstado = ({ candidato }) => {
  const { estado, vigente, kg, superado_por: superadoPor } = candidato;
  const base = { ...theme.badge, textTransform: 'none', whiteSpace: 'nowrap' };
  if (estado === 'nuevo') {
    return <span style={{ ...base, background: '#dcfce7', color: '#166534' }}>Nuevo récord</span>;
  }
  if (estado === 'supera') {
    const diferencia = vigente ? Math.round((kg - vigente.kg) * 100) / 100 : null;
    return (
      <span style={{ ...base, background: '#dbeafe', color: '#1e40af' }}>
        Supera{diferencia != null ? ` (+${diferencia} kg)` : ''}
      </span>
    );
  }
  if (estado === 'ya_registrado') {
    return <span style={{ ...base, background: '#f3f4f6', color: '#374151' }}>Ya registrado</span>;
  }
  if (estado === 'superado_en_meet') {
    return (
      <span style={{ ...base, background: '#f3f4f6', color: '#374151' }} title={superadoPor ? `${superadoPor.nombre}: ${formatearKg(superadoPor.kg)}` : undefined}>
        Otro atleta hizo más
      </span>
    );
  }
  return <span style={{ ...base, background: '#fef3c7', color: '#92400e' }}>No supera</span>;
};

const OrigenCandidato = ({ candidato }) => {
  if (candidato.origen === 'liftingcast') {
    const ambitos = (candidato.banderas || []).map((b) => AMBITOS[b] || b).join(', ');
    return <div style={estilos.secundario}>Marcado en LiftingCast{ambitos ? ` · ${ambitos}` : ''}</div>;
  }
  if (candidato.origen === 'mejor_del_meet') {
    return <div style={estilos.secundario}>Mejor marca del meet · categoría sin récord</div>;
  }
  return <div style={estilos.secundario}>Supera el récord vigente</div>;
};

const capitalizar = (texto) => (texto || '')
  .toLowerCase()
  .split(/\s+/)
  .filter(Boolean)
  .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
  .join(' ');

/** Prellena el formulario con lo que trae LiftingCast: primer nombre, resto como apellidos. */
const atletaInicial = (candidato) => {
  const [nombre = '', ...apellidos] = capitalizar(candidato.nombre_liftingcast).split(' ');
  const [dia, mes, anio] = (candidato.fecha_nacimiento || '').split('/');
  return {
    nombre,
    apellido: apellidos.join(' '),
    rut: '',
    fechaNacimiento: dia && mes && anio ? `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}` : '',
    genero: candidato.division === 'Femenino' ? 'F' : 'M',
  };
};

const TarjetaPersona = ({ persona, activa, sugerida, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{ ...estilos.persona, ...(activa ? estilos.personaActiva : {}) }}
    aria-pressed={activa}
  >
    <span>
      <span style={{ fontWeight: 600 }}>{persona.nombre_completo}</span>
      {sugerida && (
        <span style={{ ...theme.badge, textTransform: 'none', marginLeft: 8, background: '#dcfce7', color: '#166534' }}>
          Propuesto
        </span>
      )}
      <div style={estilos.secundario}>
        RUT {persona.rut}
        {persona.fecha_nacimiento ? ` · Nac. ${persona.fecha_nacimiento.split('-').reverse().join('/')}` : ''}
        {persona.numero_afiliacion
          ? ` · Afiliación N° ${persona.numero_afiliacion}${persona.afiliacion_vigente ? '' : ' (vencida)'}`
          : ' · Sin afiliación'}
      </div>
    </span>
    <span style={{ ...theme.badge, textTransform: 'none', background: '#f3f4f6', color: '#374151' }}>{persona.tipo}</span>
  </button>
);

const ModalAsociar = ({ candidato, sugerencia, seleccionActual, onCerrar, onAsociar }) => {
  const [buscar, setBuscar] = useState(busquedaInicial(candidato.nombre_liftingcast));
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccion, setSeleccion] = useState(seleccionActual || sugerencia || null);
  const [creando, setCreando] = useState(false);
  const [nuevo, setNuevo] = useState(() => atletaInicial(candidato));
  const [guardandoNuevo, setGuardandoNuevo] = useState(false);
  const [errorNuevo, setErrorNuevo] = useState(null);

  const handleCrear = async (e) => {
    e.preventDefault();
    setGuardandoNuevo(true);
    setErrorNuevo(null);
    try {
      const { persona, existente, mensaje } = await crearAtletaParaRecord(nuevo);
      if (existente) {
        setSeleccion(persona);
        setCreando(false);
        setErrorNuevo(`${mensaje}: ${persona.nombre_completo}. Quedó seleccionado; revisa y presiona Asociar.`);
        return;
      }
      onAsociar(persona);
    } catch (err) {
      setErrorNuevo(err.message);
    } finally {
      setGuardandoNuevo(false);
    }
  };

  useEffect(() => {
    let vigente = true;
    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const items = await buscarPersonasParaRecord(buscar);
        if (vigente) setResultados(items);
      } catch {
        if (vigente) setResultados([]);
      } finally {
        if (vigente) setBuscando(false);
      }
    }, 300);
    return () => {
      vigente = false;
      clearTimeout(timer);
    };
  }, [buscar]);

  const personas = useMemo(() => {
    const extra = seleccion && (!sugerencia || seleccion.rut !== sugerencia.rut) ? [seleccion] : [];
    const ocultos = new Set([sugerencia && sugerencia.rut, ...extra.map((p) => p.rut)]);
    return [...extra, ...resultados.filter((p) => !ocultos.has(p.rut))];
  }, [sugerencia, seleccion, resultados]);

  const seccion = { fontSize: 13, fontWeight: 700, color: '#374151', margin: '16px 0 8px', textTransform: 'uppercase' };

  return (
    <AdminModal open onClose={onCerrar} title="Asociar atleta" width={560}>
      <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
        <div style={{ fontWeight: 700 }}>{candidato.nombre_liftingcast}</div>
        <div style={estilos.secundario}>
          {[
            candidato.equipo,
            candidato.fecha_nacimiento && `Nac. ${candidato.fecha_nacimiento}`,
            candidato.peso_corporal && `${candidato.peso_corporal} kg corporal`,
          ].filter(Boolean).join(' · ') || 'Sin datos adicionales en LiftingCast'}
        </div>
      </div>

      {errorNuevo && <div style={theme.error}>{errorNuevo}</div>}

      {creando ? (
        <form onSubmit={handleCrear}>
          <div style={seccion}>Crear atleta nuevo</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={estilos.campoMeet}>
              Nombre *
              <input style={theme.input} value={nuevo.nombre} required
                onChange={(e) => setNuevo((n) => ({ ...n, nombre: e.target.value }))} />
            </label>
            <label style={estilos.campoMeet}>
              Apellidos *
              <input style={theme.input} value={nuevo.apellido} required
                onChange={(e) => setNuevo((n) => ({ ...n, apellido: e.target.value }))} />
            </label>
            <label style={estilos.campoMeet}>
              RUT *
              <input style={theme.input} value={nuevo.rut} required placeholder="12.345.678-5"
                onChange={(e) => setNuevo((n) => ({ ...n, rut: e.target.value }))} />
            </label>
            <label style={estilos.campoMeet}>
              Fecha de nacimiento
              <input type="date" style={theme.input} value={nuevo.fechaNacimiento}
                onChange={(e) => setNuevo((n) => ({ ...n, fechaNacimiento: e.target.value }))} />
            </label>
            <label style={estilos.campoMeet}>
              Género
              <select style={theme.input} value={nuevo.genero}
                onChange={(e) => setNuevo((n) => ({ ...n, genero: e.target.value }))}>
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
              </select>
            </label>
          </div>
          <p style={{ ...estilos.secundario, marginTop: 10 }}>
            Queda como atleta importado. Cuando se registre en ACHIPO con este RUT, la cuenta pasa a ser suya con sus récords.
          </p>
          <div style={theme.btnRow}>
            <button type="button" style={theme.btnGhost} onClick={() => setCreando(false)}>Volver</button>
            <button type="submit" style={theme.btnPrimary} disabled={guardandoNuevo}>
              {guardandoNuevo ? 'Creando...' : 'Crear y asociar'}
            </button>
          </div>
        </form>
      ) : (
        <>
          <div style={{ ...seccion, marginTop: 0 }}>Nombre propuesto</div>
          {sugerencia ? (
            <TarjetaPersona
              persona={sugerencia}
              sugerida
              activa={seleccion && seleccion.rut === sugerencia.rut}
              onClick={() => setSeleccion(sugerencia)}
            />
          ) : (
            <p style={{ ...estilos.secundario, fontSize: 13 }}>No encontramos un usuario con este nombre.</p>
          )}

          <div style={seccion}>Buscar otro usuario</div>
          <input
            type="text"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Buscar por nombre, apellido, RUT o N° de afiliación..."
            style={theme.input}
            aria-label="Buscar usuario o atleta"
          />
          <div style={{ ...estilos.listaPersonas, maxHeight: 220 }}>
            {buscando && personas.length === 0 && <AdminSpinner label="Buscando..." />}
            {!buscando && personas.length === 0 && (
              <p style={{ ...estilos.secundario, fontSize: 13 }}>No hay otros usuarios que coincidan con la búsqueda.</p>
            )}
            {personas.map((p) => (
              <TarjetaPersona
                key={p.rut}
                persona={p}
                activa={seleccion && seleccion.rut === p.rut}
                onClick={() => setSeleccion(p)}
              />
            ))}
          </div>

          <div style={seccion}>¿No está registrado?</div>
          <button type="button" style={theme.btnSecondary} onClick={() => { setCreando(true); setErrorNuevo(null); }}>
            + Crear atleta nuevo
          </button>

          <div style={theme.btnRow}>
            <button type="button" style={theme.btnGhost} onClick={onCerrar}>Cancelar</button>
            <button
              type="button"
              style={{ ...theme.btnPrimary, opacity: seleccion ? 1 : 0.5 }}
              disabled={!seleccion}
              onClick={() => onAsociar(seleccion)}
            >
              Asociar
            </button>
          </div>
        </>
      )}
    </AdminModal>
  );
};

const RecordsLiftingCastView = ({ onBack }) => {
  const { showToast } = useToast();
  const [url, setUrl] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [meet, setMeet] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [sugerencias, setSugerencias] = useState({});
  const [lugar, setLugar] = useState('');
  const [fechaRecord, setFechaRecord] = useState('');
  const [asociados, setAsociados] = useState({});
  const [excluidos, setExcluidos] = useState(() => new Set());
  const [verReferencia, setVerReferencia] = useState(false);
  const [competencia, setCompetencia] = useState('ambos');
  const [modalCandidato, setModalCandidato] = useState(null);
  const [filaResaltada, setFilaResaltada] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const filasRef = useRef({});

  const todosAplicables = useMemo(
    () => candidatos.filter((c) => ESTADOS_APLICABLES.includes(c.estado)),
    [candidatos]
  );
  const enFiltro = useCallback(
    (c) => competencia === 'ambos' || c.tipo_competencia === competencia,
    [competencia]
  );
  const aplicables = useMemo(() => todosAplicables.filter(enFiltro), [todosAplicables, enFiltro]);
  const referencia = useMemo(
    () => candidatos.filter((c) => !ESTADOS_APLICABLES.includes(c.estado) && enFiltro(c)),
    [candidatos, enFiltro]
  );
  const incluidos = useMemo(() => aplicables.filter((c) => !excluidos.has(c.id)), [aplicables, excluidos]);
  const incluidosAsociados = incluidos.filter((c) => asociados[c.lifter_id]);
  const pendientes = incluidos.length - incluidosAsociados.length;
  const puedeGuardar = incluidos.length > 0 && pendientes === 0 && lugar.trim() && !guardando;

  const handleSincronizar = async (e) => {
    e.preventDefault();
    const normalizada = normalizarUrlLiftingCast(url);
    if (!normalizada) {
      setError('Pega una URL de LiftingCast válida, por ejemplo https://archive.liftingcast.com/meets/mle6s8hzkh35/results');
      return;
    }
    setCargando(true);
    setError(null);
    try {
      const datos = await previsualizarRecordsLiftingCast(normalizada);
      setMeet(datos.meet);
      setCandidatos(datos.candidatos);
      setSugerencias(datos.sugerencias);
      setLugar(datos.meet?.lugar_sugerido || '');
      setFechaRecord(datos.meet?.fecha_record || '');
      setAsociados({});
      setExcluidos(new Set());
      const primero = datos.candidatos.find((c) => ESTADOS_APLICABLES.includes(c.estado));
      setFilaResaltada(primero ? primero.id : null);
      setUrl(datos.meet?.url || normalizada);
    } catch (err) {
      setError(err.message || 'No se pudo leer la competencia en LiftingCast');
      setMeet(null);
      setCandidatos([]);
    } finally {
      setCargando(false);
    }
  };

  const toggleIncluido = (id) => {
    setExcluidos((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });
  };

  const irASiguientePendiente = useCallback((desdeId, nuevosAsociados) => {
    const indice = incluidos.findIndex((c) => c.id === desdeId);
    const orden = [...incluidos.slice(indice + 1), ...incluidos.slice(0, Math.max(indice, 0))];
    const siguiente = orden.find((c) => !nuevosAsociados[c.lifter_id]);
    setFilaResaltada(siguiente ? siguiente.id : null);
    if (siguiente && filasRef.current[siguiente.id]) {
      filasRef.current[siguiente.id].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [incluidos]);

  const handleAsociar = (persona) => {
    const candidato = modalCandidato;
    const nuevos = { ...asociados, [candidato.lifter_id]: persona };
    setAsociados(nuevos);
    setModalCandidato(null);
    irASiguientePendiente(candidato.id, nuevos);
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const resultado = await aplicarRecordsLiftingCast({
        url,
        lugar: lugar.trim(),
        fechaRecord: fechaRecord.trim(),
        asociaciones: incluidos.map((c) => ({ candidato_id: c.id, usuario_rut: asociados[c.lifter_id].rut })),
      });
      const omitidos = resultado.omitidos.length;
      showToast(
        omitidos ? `${resultado.mensaje} ${omitidos} no se guardaron porque ya no superaban el récord vigente.` : resultado.mensaje,
        'success'
      );
      onBack();
    } catch (err) {
      setError(err.message || 'No se pudieron guardar los récords');
      showToast(err.message || 'No se pudieron guardar los récords', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const renderFila = (c, esReferencia) => {
    const persona = asociados[c.lifter_id];
    const incluido = !excluidos.has(c.id);
    const resaltada = !esReferencia && filaResaltada === c.id;
    return (
      <tr
        key={c.id}
        ref={(el) => { filasRef.current[c.id] = el; }}
        style={{
          ...(resaltada ? estilos.filaResaltada : {}),
          ...(esReferencia || !incluido ? estilos.filaInactiva : {}),
        }}
      >
        <td style={theme.td}>
          {!esReferencia && (
            <input
              type="checkbox"
              checked={incluido}
              onChange={() => toggleIncluido(c.id)}
              aria-label={`Incluir ${TIPOS[c.tipo_record]} de ${c.nombre_liftingcast}`}
            />
          )}
        </td>
        <td style={theme.td}>
          <div style={{ fontWeight: 600 }}>{c.nombre_liftingcast}</div>
          <div style={estilos.secundario}>
            {[c.equipo, c.peso_corporal && `${c.peso_corporal} kg corporal`].filter(Boolean).join(' · ')}
          </div>
          <div style={estilos.secundario}>{c.division_liftingcast}</div>
        </td>
        <td style={theme.td}>
          <div>
            {c.categoria_peso} · {c.division === 'Femenino' ? 'Mujeres' : 'Hombres'} · {EDADES[c.categoria_edad] || c.categoria_edad}
          </div>
          <div style={estilos.secundario}>
            {MODALIDADES[c.modalidad] || c.modalidad} · {c.tipo_competencia === 'tested' ? 'Tested' : 'Untested'}
          </div>
        </td>
        <td style={theme.td}>{TIPOS[c.tipo_record] || c.tipo_record}</td>
        <td style={theme.td}>
          <div style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatearKg(c.kg)}</div>
          <div style={estilos.secundario}>{c.intento ? `${c.intento}º intento` : 'Suma SQ + BP + DL'}</div>
        </td>
        <td style={theme.td}>
          {c.vigente ? (
            <>
              <div style={{ fontVariantNumeric: 'tabular-nums' }}>{formatearKg(c.vigente.kg)}</div>
              <div style={estilos.secundario}>
                {[c.vigente.nombre_atleta, c.vigente.fecha_record].filter(Boolean).join(' · ')}
              </div>
            </>
          ) : (
            <span style={{ color: '#6b7280' }}>Sin récord</span>
          )}
        </td>
        <td style={theme.td}>
          <BadgeEstado candidato={c} />
          <OrigenCandidato candidato={c} />
        </td>
        <td style={theme.td}>
          {esReferencia ? (
            <span style={{ color: '#9ca3af' }}>—</span>
          ) : persona ? (
            <div>
              <div style={{ fontWeight: 600, color: '#166534' }}>✓ {persona.nombre_completo}</div>
              <div style={estilos.secundario}>
                {persona.tipo} · RUT {persona.rut}
              </div>
              <button
                type="button"
                onClick={() => setModalCandidato(c)}
                style={{ ...theme.btnGhost, padding: '4px 10px', fontSize: 12, marginTop: 4 }}
              >
                Cambiar
              </button>
            </div>
          ) : (
            <button
              type="button"
              style={{ ...theme.btnPrimary, whiteSpace: 'nowrap', opacity: incluido ? 1 : 0.5 }}
              disabled={!incluido}
              onClick={() => setModalCandidato(c)}
            >
              Asociar atleta
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div style={{ ...theme.container, maxWidth: 1320 }}>
      <nav style={theme.breadcrumb}>
        <button
          type="button"
          onClick={onBack}
          style={{ ...theme.breadcrumbLink, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
        >
          Récords
        </button>
        <span style={theme.breadcrumbSep}>/</span>
        <span style={theme.breadcrumbCurrent}>Sincronizar con LiftingCast</span>
      </nav>

      <div style={theme.pageHeader}>
        <div>
          <h1 style={theme.title}>Sincronizar récords con LiftingCast</h1>
          <p style={theme.subtitle}>
            Pega el link de resultados de la competencia, revisa los récords encontrados y asocia cada uno a un usuario o atleta.
          </p>
        </div>
        <button type="button" style={theme.btnSecondary} onClick={onBack}>← Volver a récords</button>
      </div>

      <form style={estilos.formSync} onSubmit={handleSincronizar}>
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://archive.liftingcast.com/meets/mle6s8hzkh35/results"
          style={{ ...theme.input, flex: 1, minWidth: 320 }}
          aria-label="URL de la competencia en LiftingCast"
          disabled={cargando}
        />
        <button type="submit" style={theme.btnPrimary} disabled={cargando || !url.trim()}>
          {cargando ? 'Sincronizando...' : 'Sincronizar'}
        </button>
        <p style={estilos.ayuda}>
          Sirve el link de liftingcast.com o de archive.liftingcast.com. No se guarda nada hasta que presiones «Actualizar récords».
        </p>
      </form>

      {error && <div style={theme.error}>{error}</div>}
      {cargando && <AdminSpinner label="Leyendo la competencia en LiftingCast..." />}

      {!cargando && meet && (
        <>
          <div style={estilos.meetCard}>
            <div>
              <h2 style={estilos.meetNombre}>{meet.nombre || meet.meet_id}</h2>
              <div style={estilos.meetMeta}>
                {[meet.fecha_texto, meet.federacion, meet.evento_nombre && `Evento: ${meet.evento_nombre}`].filter(Boolean).join(' · ')}
              </div>
              <div style={estilos.meetMeta}>
                {todosAplicables.length} récords para actualizar
                {candidatos.length > todosAplicables.length
                  ? ` · ${candidatos.length - todosAplicables.length} marcados que no superan el vigente`
                  : ''}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <label style={estilos.campoMeet}>
                  Lugar *
                  <input
                    type="text"
                    value={lugar}
                    onChange={(e) => setLugar(e.target.value)}
                    placeholder="Ej. Quintero, Chile"
                    style={{ ...theme.input, width: 220, borderColor: lugar.trim() ? undefined : '#f59e0b' }}
                  />
                </label>
                <label style={estilos.campoMeet}>
                  Fecha del récord
                  <input
                    type="text"
                    value={fechaRecord}
                    onChange={(e) => setFechaRecord(e.target.value)}
                    placeholder="Ej. Marzo 2026"
                    style={{ ...theme.input, width: 160 }}
                  />
                </label>
              </div>
              <div style={estilos.campoMeet}>
                Récords a asociar
                <div style={estilos.filtroCompetencia} role="group" aria-label="Filtrar por tested o untested">
                  {FILTROS_COMPETENCIA.map((f) => {
                    const cantidad = f.id === 'ambos'
                      ? todosAplicables.length
                      : todosAplicables.filter((c) => c.tipo_competencia === f.id).length;
                    const activo = competencia === f.id;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        aria-pressed={activo}
                        onClick={() => setCompetencia(f.id)}
                        style={{ ...estilos.opcionFiltro, ...(activo ? estilos.opcionFiltroActiva : {}) }}
                      >
                        {f.label} ({cantidad})
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {candidatos.length === 0 ? (
            <p style={theme.empty}>
              Esta competencia no tiene intentos marcados como récord en LiftingCast ni marcas que superen los récords vigentes.
            </p>
          ) : aplicables.length === 0 && referencia.length === 0 ? (
            <p style={theme.empty}>
              No hay récords {competencia === 'tested' ? 'tested' : 'untested'} en esta competencia.
            </p>
          ) : (
            <div style={theme.card}>
              <div style={{ overflowX: 'auto' }}>
                <table style={theme.table}>
                  <thead>
                    <tr>
                      <th style={theme.th} aria-label="Incluir" />
                      <th style={theme.th}>Atleta (LiftingCast)</th>
                      <th style={theme.th}>Categoría</th>
                      <th style={theme.th}>Tipo</th>
                      <th style={theme.th}>Marca</th>
                      <th style={theme.th}>Récord vigente</th>
                      <th style={theme.th}>Estado</th>
                      <th style={theme.th}>Asociar atleta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aplicables.map((c) => renderFila(c, false))}
                    {verReferencia && referencia.map((c) => renderFila(c, true))}
                  </tbody>
                </table>
              </div>
              {referencia.length > 0 && (
                <button
                  type="button"
                  style={{ ...theme.btnGhost, marginTop: 12 }}
                  onClick={() => setVerReferencia((v) => !v)}
                >
                  {verReferencia
                    ? 'Ocultar los que no superan el récord vigente'
                    : `Ver ${referencia.length} marcados en LiftingCast que no superan el récord vigente`}
                </button>
              )}
            </div>
          )}

          {aplicables.length > 0 && (
            <div style={estilos.barraGuardar}>
              <div style={{ fontSize: 14, color: '#374151' }}>
                <strong>{incluidosAsociados.length}</strong> de <strong>{incluidos.length}</strong> récords
                {competencia === 'ambos' ? '' : ` ${competencia}`} asociados
                {pendientes > 0 && <span style={{ color: '#92400e' }}> · faltan {pendientes} por asociar</span>}
                {!lugar.trim() && <span style={{ color: '#92400e' }}> · falta indicar el lugar</span>}
              </div>
              <button
                type="button"
                style={{ ...theme.btnPrimary, opacity: puedeGuardar ? 1 : 0.5 }}
                disabled={!puedeGuardar}
                onClick={handleGuardar}
              >
                {guardando ? 'Guardando...' : `Actualizar récords (${incluidos.length})`}
              </button>
            </div>
          )}
        </>
      )}

      {modalCandidato && (
        <ModalAsociar
          candidato={modalCandidato}
          sugerencia={sugerencias[modalCandidato.lifter_id]}
          seleccionActual={asociados[modalCandidato.lifter_id]}
          onCerrar={() => setModalCandidato(null)}
          onAsociar={handleAsociar}
        />
      )}
    </div>
  );
};

export default RecordsLiftingCastView;
